import styled from '@emotion/styled'
import { ActionButton } from 'modules/ui/atoms/ActionButton'
import { DEFAULT_FONT_STYLES, DEFAULT_THEME_COLORS } from 'css/theme'
import {
  PrevStepButton,
  STEP_LAYOUT_BACKGROUND_COLOR,
  STEP_LAYOUT_BORDER_RADIUS,
  STEP_LAYOUT_HEIGHT,
  STEP_LAYOUT_LARGE_WIDTH,
  STEP_LAYOUT_SMALL_WIDTH,
} from '../shared/shared-step-layout.styled'
import Paragraph from 'modules/ui/atoms/Typography/Paragraph'

export const Row = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  min-width: 320px;

  width: 100%;

  form {
    width: 100%;
  }

  @media (min-width: ${STEP_LAYOUT_SMALL_WIDTH}) {
    width: ${STEP_LAYOUT_SMALL_WIDTH};
    border-radius: ${STEP_LAYOUT_BORDER_RADIUS};
  }

  @media (min-width: 784px) {
    flex-direction: row;
    width: ${STEP_LAYOUT_LARGE_WIDTH};
    max-height: ${STEP_LAYOUT_HEIGHT};
    height: calc(100vh - 82px);
  }
`

export const Center = styled.div`
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  max-width: 100%;
  min-width: 320px;
  background: ${STEP_LAYOUT_BACKGROUND_COLOR};
  border-top-left-radius: ${STEP_LAYOUT_BORDER_RADIUS};
  border-top-right-radius: ${STEP_LAYOUT_BORDER_RADIUS};

  width: 100%;

  form {
    width: 100%;
  }

  @media (min-width: ${STEP_LAYOUT_SMALL_WIDTH}) {
    width: ${STEP_LAYOUT_SMALL_WIDTH};
    border-radius: ${STEP_LAYOUT_BORDER_RADIUS};
  }

  @media (min-width: 784px) {
    width: ${STEP_LAYOUT_SMALL_WIDTH};
  }
`

export const Left = styled.div`
  overflow: hidden;
  position: relative;
  min-height: ${STEP_LAYOUT_HEIGHT};
  background: ${STEP_LAYOUT_BACKGROUND_COLOR};
  box-sizing: border-box;
  border-top-left-radius: ${STEP_LAYOUT_BORDER_RADIUS};
  border-top-right-radius: ${STEP_LAYOUT_BORDER_RADIUS};

  @media (min-width: 784px) {
    width: ${STEP_LAYOUT_SMALL_WIDTH};
    border-top-right-radius: 0;
    border-top-left-radius: ${STEP_LAYOUT_BORDER_RADIUS};
    border-bottom-left-radius: ${STEP_LAYOUT_BORDER_RADIUS};
  }
`

export const Right = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  background: ${STEP_LAYOUT_BACKGROUND_COLOR};

  @media (min-width: 784px) {
    border-top-right-radius: ${STEP_LAYOUT_BORDER_RADIUS};
    border-bottom-right-radius: ${STEP_LAYOUT_BORDER_RADIUS};
  }
`

export const Outer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 82px);
  max-height: ${STEP_LAYOUT_HEIGHT};
`

export const Body = styled.main`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding: 0 24px;
  line-height: ${DEFAULT_FONT_STYLES['--theme-font--body-default__line-height']};
`

export const CenteredBody = styled.div`
  flex: 1;
  flex-direction: column;
  display: flex;
  padding: 0 24px;
  align-items: center;
  justify-content: center;

  > div {
    width: 100%;
  }
`

export const Footer = styled.footer`
  gap: 16px;
  display: flex;
  flex-direction: column;
  padding: 12px 24px 24px;
`

export const FooterText = styled(Paragraph)`
  text-align: center;
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-20']};
  font-size: ${DEFAULT_FONT_STYLES['--theme-font--caption__font-size']};
  font-weight: ${DEFAULT_FONT_STYLES['--theme-font--caption__font-weight']};
  line-height: ${DEFAULT_FONT_STYLES['--theme-font--caption__line-height']};

  a {
    color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-20']};
  }
`

export const FooterButton = styled(ActionButton)``

interface HeaderLogoProps {
  src: string
  alt: string
}

interface HeaderProps {
  title?: React.ReactNode
  logo?: HeaderLogoProps
  hideBackButton?: boolean
  backButtonDataId?: string
  titleSize?: 'sm' | 'md' | 'lg'
  titleAlign?: 'left' | 'center' | 'right'
  tabIndex?: number
  marginTop?: string
}

interface HeaderTitleProps {
  size?: 'sm' | 'md' | 'lg'
  textAlign?: 'left' | 'center' | 'right'
  marginTop?: string
}

export const Header = ({
  logo,
  title,
  hideBackButton,
  backButtonDataId,
  titleSize,
  titleAlign,
  tabIndex,
  marginTop,
}: HeaderProps) => (
  <HeaderOuter tabIndex={tabIndex}>
    {hideBackButton ? null : (
      <PrevStepButton iconName="arrow_back" size="lg" dataId={backButtonDataId} />
    )}
    {logo && <HeaderLogo src={logo.src} alt={logo.alt} />}
    {title && (
      <HeaderTitle size={titleSize} textAlign={titleAlign} marginTop={marginTop}>
        {title}
      </HeaderTitle>
    )}
  </HeaderOuter>
)

const HeaderOuter = styled.header`
  display: flex;
  align-items: center;
  position: relative;
  padding: 20px 24px 8px;
  @media (max-width: 768px) {
    margin-top: 20px;
  }
`

export const HeaderTitle = styled.div<HeaderTitleProps>`
  flex: 1;
  text-align: ${({ textAlign }) => textAlign || 'center'};
  font-weight: ${DEFAULT_FONT_STYLES['--theme-font--title-page__font-weight']};
  line-height: ${({ size }) => {
    if (size === 'lg') return DEFAULT_FONT_STYLES['--theme-font--title-large-page__line-height']
    else if (size === 'sm') return DEFAULT_FONT_STYLES['--theme-font--title-page-sm__line-height']
    return DEFAULT_FONT_STYLES['--theme-font--title-page__line-height']
  }};
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-20']};
  font-size: ${({ size }) => {
    if (size === 'lg') return DEFAULT_FONT_STYLES['--theme-font--title-large-page__font-size']
    else if (size === 'md') return DEFAULT_FONT_STYLES['--theme-font--title-medium-page__font-size']
    else if (size === 'sm') return DEFAULT_FONT_STYLES['--theme-font--title-page-sm__font-size']
    return DEFAULT_FONT_STYLES['--theme-font--title-page-sm__font-size']
  }};
  margin-top: ${({ marginTop }) => marginTop};
`

export const HeaderLogo = styled.img`
  height: 44px;
  display: block;
  max-width: 100%;
`
